<template>
  <!-- Intro animation shown as the first step of the Sistema PIE demo chatbot.
       It auto-cycles through the platform-wide capabilities (massive student
       load via a spreadsheet, info management, simultaneous editing, monthly
       usage reports, information shared between documents, sending the FUDEI
       directly, and more). Self-contained: scoped styles + prefixed `pii-`
       keyframes, auto-advance with hover pause, prefers-reduced-motion fallback,
       and timer cleanup in beforeDestroy. -->
  <div
    class="pie-intro"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <div class="pii-stage">
      <div class="pii-scene-wrap" aria-hidden="true">
        <transition name="pii-fade" mode="out-in">
          <!-- Carga masiva: estudiantes cargados desde un Excel a la tabla -->
          <div v-if="current.scene === 'planilla'" key="planilla" class="pii-scene pii-load">
            <div class="pii-excel">
              <span class="pii-excel-tag">XLS</span>
              <i></i><i></i><i></i><i></i>
            </div>
            <div class="pii-feed">➜</div>
            <div class="pii-table">
              <div class="pii-table-head">
                <span>Estudiante</span><span>Curso</span>
              </div>
              <div
                class="pii-trow"
                v-for="(s, i) in demoStudents"
                :key="i"
                :style="{ '--d': i * 0.55 + 's' }"
              >
                <span class="pii-avatar"></span>
                <span class="pii-trow-name">{{ s.name }}</span>
                <span class="pii-trow-grade">{{ s.grade }}</span>
              </div>
            </div>
          </div>

          <!-- Gestiona la información: ficha de un niño en edición -->
          <div v-else-if="current.scene === 'manage'" key="manage" class="pii-scene">
            <div class="pii-profile">
              <div class="pii-profile-top">
                <div class="pii-photo">🧒</div>
                <div class="pii-profile-name">
                  <strong>Martina Rojas</strong>
                  <i class="w50"></i>
                </div>
              </div>
              <div class="pii-field" v-for="f in 3" :key="f">
                <i class="pii-field-fill" :style="{ animationDelay: f * 0.25 + 's' }"></i>
              </div>
              <div class="pii-save">Guardar ✓</div>
              <div class="pii-pencil">✎</div>
            </div>
          </div>

          <!-- Trabajo simultáneo: cada usuario bloquea un campo distinto -->
          <div v-else-if="current.scene === 'simultaneous'" key="simultaneous" class="pii-scene">
            <div class="pii-form">
              <div class="pii-fieldrow owned-a">
                <span class="pii-owner a">A</span>
                <i class="pii-typing"><b class="pii-caret one"></b></i>
                <span class="pii-lock">🔒</span>
              </div>
              <div class="pii-fieldrow">
                <i></i>
              </div>
              <div class="pii-fieldrow owned-b">
                <span class="pii-owner b">M</span>
                <i class="pii-typing"><b class="pii-caret two"></b></i>
                <span class="pii-lock">🔒</span>
              </div>
              <div class="pii-fieldrow">
                <i></i>
              </div>
            </div>
          </div>

          <!-- Reportes mensuales de uso -->
          <div v-else-if="current.scene === 'reports'" key="reports" class="pii-scene">
            <div class="pii-report">
              <div class="pii-report-head">
                <span class="pii-cal">📅</span>
                <span class="pii-trend">▲</span>
              </div>
              <div class="pii-bars">
                <span style="--h: 38%; --d: 0s"></span>
                <span style="--h: 62%; --d: 0.12s"></span>
                <span style="--h: 50%; --d: 0.24s"></span>
                <span style="--h: 82%; --d: 0.36s"></span>
                <span style="--h: 70%; --d: 0.48s"></span>
                <span style="--h: 95%; --d: 0.6s"></span>
              </div>
            </div>
          </div>

          <!-- Información compartida: un documento se llena y el otro la recibe -->
          <div v-else-if="current.scene === 'share'" key="share" class="pii-scene">
            <div class="pii-share">
              <div class="pii-mini-doc left">
                <span class="pii-doc-cap">Origen</span>
                <i class="pii-fillbar" style="--d: 0s"></i>
                <i class="pii-fillbar" style="--d: 0.3s"></i>
                <i class="pii-fillbar short" style="--d: 0.6s"></i>
              </div>
              <div class="pii-link">
                <span class="pii-packet"></span>
              </div>
              <div class="pii-mini-doc right">
                <span class="pii-doc-cap">Destino</span>
                <i class="pii-recvbar" style="--d: 1.2s"></i>
                <i class="pii-recvbar short" style="--d: 1.5s"></i>
                <i class="pii-recvbar" style="--d: 1.8s"></i>
              </div>
            </div>
          </div>

          <!-- Envía el FUDEI directamente -->
          <div v-else-if="current.scene === 'fudei'" key="fudei" class="pii-scene">
            <div class="pii-send">
              <div class="pii-fudei-doc">
                <span class="pii-fudei-tag">FUDEI</span>
                <i></i><i></i><i class="short"></i>
              </div>
              <div class="pii-plane">✈</div>
              <div class="pii-gov">
                <span class="pii-gov-roof"></span>
                Mineduc
              </div>
            </div>
          </div>
        </transition>
      </div>

      <div class="pii-caption-wrap">
        <transition name="pii-cap" mode="out-in">
          <div class="pii-caption" :key="active">
            <div class="pii-icon">{{ current.icon }}</div>
            <h4 v-html="current.title"></h4>
            <p v-html="current.text"></p>
          </div>
        </transition>
      </div>
    </div>

    <div class="pii-dots">
      <button
        v-for="(f, i) in features"
        :key="i"
        type="button"
        class="pii-dot"
        :class="{ active: i === active }"
        :aria-label="f.title"
        @click.stop="go(i)"
      ></button>
    </div>

    <div class="pii-foot">
      Todo en una sola plataforma, con <strong>usos ilimitados</strong> para tu colegio.
    </div>
  </div>
</template>

<script>
const FEATURES = [
  {
    scene: "planilla",
    icon: "📋",
    title: "Carga masiva con una planilla",
    text: "El administrador sube a <strong>todos los estudiantes de una sola vez</strong> desde una planilla, sin ingresarlos uno por uno.",
  },
  {
    scene: "manage",
    icon: "🗂️",
    title: "Gestiona la información",
    text: "Cada profesional <strong>administra y actualiza los datos</strong> de sus estudiantes en un solo lugar, siempre al día.",
  },
  {
    scene: "simultaneous",
    icon: "👥",
    title: "Trabajen al mismo tiempo",
    text: "Varios usuarios editan el <strong>mismo documento simultáneamente</strong>, sin pisarse ni duplicar el trabajo.",
  },
  {
    scene: "reports",
    icon: "📈",
    title: "Reportes mensuales de uso",
    text: "Recibe cada mes un <strong>reporte del avance y del uso</strong> de la plataforma en tu colegio.",
  },
  {
    scene: "share",
    icon: "🔗",
    title: "Información compartida entre documentos",
    text: "Los datos se <strong>transfieren solos entre documentos</strong>, sin copiar y pegar una y otra vez.",
  },
  {
    scene: "fudei",
    icon: "📤",
    title: "Envía el FUDEI directo desde el sitio",
    text: "Genera y <strong>envía el FUDEI</strong> a la plataforma del Mineduc directamente desde Utopie.",
  },
];

export default {
  name: "PieIntroAnimation",
  // Absorb the props ChatMessageComponent binds on the custom component so they
  // don't leak onto the root element. None are needed by the animation.
  props: {
    condition: { type: String, required: false, default: "" },
    context: { type: Object, required: false, default: () => ({}) },
    canWork: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      features: FEATURES,
      demoStudents: [
        { name: "Isaac Alday", grade: "2°A" },
        { name: "Gaspar Arriagada", grade: "2°A" },
        { name: "Lucía Castillo", grade: "2°A" },
      ],
      active: 0,
      timer: null,
      paused: false,
      reduce: false,
      INTERVAL: 2400,
    };
  },
  computed: {
    current() {
      return this.features[this.active];
    },
  },
  mounted() {
    this.reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!this.reduce) this.startTimer();
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    startTimer() {
      this.stopTimer();
      this.timer = setInterval(() => {
        if (!this.paused) this.next();
      }, this.INTERVAL);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    next() {
      this.active = (this.active + 1) % this.features.length;
    },
    go(i) {
      this.active = i;
      if (!this.reduce) this.startTimer();
    },
    pause() {
      this.paused = true;
    },
    resume() {
      this.paused = false;
    },
  },
};
</script>

<style scoped lang="scss">
$pie: #c6588d;
$pie-2: #d98bb0;
$pie-soft: #f7e7f0;
$ink: #3a2230;

.pie-intro {
  --pie: #{$pie};
  --pie-2: #{$pie-2};
  --pie-soft: #{$pie-soft};
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  padding: 18px 18px 14px;
  background: linear-gradient(160deg, #fff 0%, #fdf4f9 100%);
  border: 1.5px solid rgba(198, 88, 141, 0.22);
  font-family: "Ubuntu", Arial, sans-serif;
  overflow: hidden;
}

.pii-stage {
  display: flex;
  align-items: center;
  gap: 18px;
  min-height: 210px;
}

.pii-scene-wrap {
  flex: 0 0 46%;
  max-width: 46%;
  height: 190px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pii-scene {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pii-caption-wrap {
  flex: 1 1 auto;
  min-width: 0;
}

.pii-caption {
  text-align: left;

  .pii-icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: var(--pie-soft);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    margin-bottom: 8px;
  }
  h4 {
    color: var(--pie);
    font-weight: 700;
    font-size: 1.05rem;
    margin: 0 0 6px;
    line-height: 1.2;
  }
  p {
    color: $ink;
    font-size: 0.92rem;
    margin: 0;
    line-height: 1.35;
  }
}

/* ---------- dots ---------- */
.pii-dots {
  display: flex;
  gap: 7px;
  justify-content: center;
  margin: 14px 0 6px;
}
.pii-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: none;
  padding: 0;
  background: rgba(198, 88, 141, 0.25);
  cursor: pointer;
  transition: all 0.25s ease;
  &.active {
    background: var(--pie);
    width: 22px;
    border-radius: 6px;
  }
}

.pii-foot {
  text-align: center;
  font-size: 0.82rem;
  color: rgba(58, 34, 48, 0.7);
  strong {
    color: var(--pie);
  }
}

/* ---------- transitions ---------- */
.pii-fade-enter-active,
.pii-cap-enter-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.pii-fade-leave-active,
.pii-cap-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.pii-fade-enter {
  opacity: 0;
  transform: scale(0.9);
}
.pii-fade-leave-to {
  opacity: 0;
  transform: scale(1.04);
}
.pii-cap-enter {
  opacity: 0;
  transform: translateY(10px);
}
.pii-cap-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* common doc primitives */
i {
  display: block;
  height: 7px;
  border-radius: 4px;
  background: rgba(198, 88, 141, 0.2);
  margin: 7px 0;
}
.w90 { width: 90%; } .w80 { width: 80%; } .w70 { width: 70%; }
.w60 { width: 60%; } .w50 { width: 50%; } .w40 { width: 40%; }
.short { width: 45%; }

.pii-avatar {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--pie-2);
  flex: 0 0 16px;
  &.big {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
  }
}

/* ---------- scene: planilla (Excel -> tabla de estudiantes) ---------- */
.pii-load {
  gap: 6px;
}
.pii-excel {
  position: relative;
  flex: 0 0 40px;
  width: 40px;
  background: #fff;
  border: 1.5px solid #cfe6d4;
  border-radius: 8px;
  padding: 12px 6px 7px;
  box-shadow: 0 6px 16px rgba(31, 157, 85, 0.16);
  animation: pii-excel-pulse 2.4s ease-in-out infinite;
  i {
    height: 5px;
    margin: 4px 0;
    background: rgba(31, 157, 85, 0.28);
  }
}
.pii-excel-tag {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  background: #1f9d55;
  color: #fff;
  font-size: 0.52rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 8px;
}
.pii-feed {
  flex: 0 0 auto;
  color: var(--pie);
  font-size: 15px;
  margin: 0 1px;
  animation: pii-feed 2.4s ease-in-out infinite;
}
.pii-table {
  flex: 1 1 auto;
  min-width: 0;
  background: #fff;
  border: 1.5px solid rgba(198, 88, 141, 0.25);
  border-radius: 8px;
  padding: 6px 7px;
  box-shadow: 0 8px 20px rgba(198, 88, 141, 0.12);
}
.pii-table-head {
  display: flex;
  justify-content: space-between;
  font-size: 0.55rem;
  font-weight: 700;
  color: var(--pie);
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(198, 88, 141, 0.18);
}
.pii-trow {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(198, 88, 141, 0.08);
  opacity: 0;
  animation: pii-rowin 2.8s ease-in-out infinite;
  animation-delay: var(--d);
  .pii-avatar {
    width: 12px;
    height: 12px;
    flex: 0 0 12px;
  }
}
.pii-trow-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.6rem;
  color: $ink;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pii-trow-grade {
  font-size: 0.55rem;
  font-weight: 700;
  color: var(--pie);
  background: var(--pie-soft);
  border-radius: 6px;
  padding: 1px 5px;
  flex: 0 0 auto;
}
@keyframes pii-rowin {
  0% { opacity: 0; transform: translateX(-14px); }
  18%, 82% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(-14px); }
}
@keyframes pii-excel-pulse {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}
@keyframes pii-feed {
  0%, 100% { transform: translateX(0); opacity: 0.5; }
  50% { transform: translateX(3px); opacity: 1; }
}
@keyframes pii-pop {
  0%, 100% { transform: translateY(0) scale(1); }
  40% { transform: translateY(-4px) scale(1.06); }
}

/* ---------- scene: manage ---------- */
.pii-profile {
  position: relative;
  width: 82%;
  background: #fff;
  border: 1.5px solid rgba(198, 88, 141, 0.25);
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 8px 22px rgba(198, 88, 141, 0.12);
}
.pii-profile-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.pii-photo {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 10px;
  background: var(--pie-soft);
  border: 2px solid var(--pie-2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}
.pii-profile-name {
  flex: 1;
  strong {
    display: block;
    font-size: 0.72rem;
    color: $ink;
    margin-bottom: 5px;
  }
}
.pii-save {
  display: inline-block;
  margin-top: 8px;
  background: var(--pie);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 14px;
  animation: pii-pop 2.6s ease-in-out infinite;
}
.pii-field {
  height: 9px;
  border-radius: 4px;
  background: rgba(198, 88, 141, 0.12);
  margin: 6px 0;
  overflow: hidden;
}
.pii-field-fill {
  display: block;
  height: 100%;
  width: 0;
  margin: 0;
  border-radius: 4px;
  background: var(--pie-2);
  animation: pii-fill 2.8s ease-in-out infinite;
}
.pii-pencil {
  position: absolute;
  right: 10px;
  bottom: 8px;
  font-size: 18px;
  color: var(--pie);
  animation: pii-write 1.6s ease-in-out infinite;
}
@keyframes pii-fill {
  0%, 100% { width: 0; }
  55%, 80% { width: 100%; }
}
@keyframes pii-write {
  0%, 100% { transform: translateX(0) rotate(0); }
  50% { transform: translateX(-6px) rotate(-12deg); }
}

/* ---------- scene: simultaneous (campos bloqueados por usuario) ---------- */
.pii-form {
  width: 84%;
  background: #fff;
  border: 1.5px solid rgba(198, 88, 141, 0.25);
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 8px 22px rgba(198, 88, 141, 0.12);
}
.pii-fieldrow {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  margin: 8px 0;
  padding: 0 8px;
  border: 1.5px solid rgba(198, 88, 141, 0.15);
  border-radius: 6px;
  background: #fbf6f9;
  i {
    flex: 1;
    margin: 0;
  }
}
.pii-fieldrow.owned-a {
  border-color: var(--pie);
  background: rgba(198, 88, 141, 0.08);
  animation: pii-glow-a 2s ease-in-out infinite;
}
.pii-fieldrow.owned-b {
  border-color: #20b2aa;
  background: rgba(32, 178, 170, 0.08);
  animation: pii-glow-b 2s ease-in-out infinite;
}
.pii-owner {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  border-radius: 50%;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  &.a { background: var(--pie); }
  &.b { background: #20b2aa; }
}
.pii-lock {
  flex: 0 0 auto;
  font-size: 11px;
  opacity: 0.65;
}
.pii-typing {
  position: relative;
  flex: 1;
  height: 7px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
  overflow: visible;
}
@keyframes pii-glow-a {
  0%, 100% { box-shadow: 0 0 0 2px rgba(198, 88, 141, 0.1); }
  50% { box-shadow: 0 0 0 3px rgba(198, 88, 141, 0.32); }
}
@keyframes pii-glow-b {
  0%, 100% { box-shadow: 0 0 0 2px rgba(32, 178, 170, 0.1); }
  50% { box-shadow: 0 0 0 3px rgba(32, 178, 170, 0.32); }
}
.pii-caret {
  position: absolute;
  top: -3px;
  width: 2px;
  height: 13px;
  border-radius: 2px;
  &.one {
    background: var(--pie);
    animation: pii-type-a 2.4s ease-in-out infinite;
  }
  &.two {
    background: #20b2aa;
    animation: pii-type-b 2.4s ease-in-out infinite;
  }
}
@keyframes pii-type-a {
  0% { left: 0; opacity: 1; }
  60% { left: 100%; opacity: 1; }
  62% { opacity: 0; }
  100% { left: 0; opacity: 0; }
}
@keyframes pii-type-b {
  0% { left: 0; opacity: 0; }
  20% { opacity: 1; }
  70% { left: 100%; opacity: 1; }
  72% { opacity: 0; }
  100% { left: 0; opacity: 0; }
}

/* ---------- scene: reports ---------- */
.pii-report {
  width: 84%;
  background: #fff;
  border: 1.5px solid rgba(198, 88, 141, 0.25);
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 8px 22px rgba(198, 88, 141, 0.12);
}
.pii-report-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 16px;
}
.pii-trend {
  color: #2bb673;
  font-size: 13px;
}
.pii-bars {
  display: flex;
  align-items: flex-end;
  gap: 7px;
  height: 92px;
  span {
    flex: 1;
    height: 0;
    border-radius: 4px 4px 0 0;
    background: linear-gradient(180deg, var(--pie) 0%, var(--pie-2) 100%);
    animation: pii-bar 2.8s ease-in-out infinite;
    animation-delay: var(--d);
  }
}
@keyframes pii-bar {
  0% { height: 0; }
  45%, 80% { height: var(--h); }
  100% { height: 0; }
}

/* ---------- scene: share ---------- */
.pii-share {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.pii-mini-doc {
  width: 64px;
  background: #fff;
  border: 1.5px solid rgba(198, 88, 141, 0.25);
  border-radius: 8px;
  padding: 9px 8px;
  box-shadow: 0 8px 18px rgba(198, 88, 141, 0.12);
  i { margin: 5px 0; }
}
.pii-link {
  position: relative;
  width: 70px;
  height: 2px;
  margin: 0 6px;
  background: repeating-linear-gradient(
    90deg,
    var(--pie-2) 0 6px,
    transparent 6px 12px
  );
}
.pii-packet {
  position: absolute;
  top: -5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--pie);
  box-shadow: 0 0 10px rgba(198, 88, 141, 0.6);
  animation: pii-travel 2.2s ease-in-out infinite;
}
@keyframes pii-travel {
  0% { left: -6px; transform: scale(0.6); opacity: 0; }
  15% { opacity: 1; transform: scale(1); }
  85% { opacity: 1; transform: scale(1); }
  100% { left: calc(100% - 6px); transform: scale(0.6); opacity: 0; }
}
.pii-share .pii-packet {
  animation-duration: 3s;
}
.pii-doc-cap {
  display: block;
  font-size: 0.5rem;
  font-weight: 700;
  color: var(--pie);
  text-align: center;
  margin-bottom: 5px;
}
.pii-fillbar,
.pii-recvbar {
  position: relative;
  height: 6px;
  margin: 5px 0;
  border-radius: 4px;
  background: rgba(198, 88, 141, 0.14);
  overflow: hidden;
}
.pii-fillbar::after,
.pii-recvbar::after {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  border-radius: 4px;
  animation: pii-fillbar 3s ease-in-out infinite;
  animation-delay: var(--d);
}
.pii-fillbar::after {
  background: var(--pie-2);
}
.pii-recvbar::after {
  background: #20b2aa;
}
@keyframes pii-fillbar {
  0% { width: 0; }
  30%, 85% { width: 100%; }
  100% { width: 0; }
}

/* ---------- scene: fudei ---------- */
.pii-send {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 92%;
  padding: 0 6px;
}
.pii-fudei-doc {
  position: relative;
  width: 70px;
  background: #fff;
  border: 1.5px solid rgba(198, 88, 141, 0.25);
  border-radius: 8px;
  padding: 14px 8px 8px;
  box-shadow: 0 8px 18px rgba(198, 88, 141, 0.12);
}
.pii-fudei-tag {
  position: absolute;
  top: -9px;
  left: 8px;
  background: var(--pie);
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 10px;
}
.pii-plane {
  font-size: 22px;
  color: var(--pie);
  animation: pii-fly 2.6s ease-in-out infinite;
}
.pii-gov {
  position: relative;
  width: 64px;
  height: 52px;
  background: var(--pie-soft);
  border-radius: 8px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 6px;
  font-size: 0.66rem;
  font-weight: 700;
  color: var(--pie);
}
.pii-gov-roof {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 36px solid transparent;
  border-right: 36px solid transparent;
  border-bottom: 14px solid var(--pie);
}
@keyframes pii-fly {
  0% { transform: translate(0, 0) rotate(0); opacity: 0.4; }
  20% { opacity: 1; }
  70% { transform: translate(14px, -10px) rotate(8deg); opacity: 1; }
  100% { transform: translate(26px, -2px) rotate(0); opacity: 0; }
}

/* ---------- mobile ---------- */
@media (max-width: 575px) {
  .pii-stage {
    flex-direction: column;
    gap: 12px;
    min-height: 300px;
  }
  .pii-scene-wrap {
    flex: 0 0 150px;
    max-width: 100%;
    width: 100%;
    height: 150px;
  }
  .pii-caption {
    text-align: center;
    .pii-icon { margin: 0 auto 8px; }
  }
}

/* ---------- reduced motion ---------- */
@media (prefers-reduced-motion: reduce) {
  .pii-excel,
  .pii-feed,
  .pii-trow,
  .pii-field-fill,
  .pii-pencil,
  .pii-save,
  .pii-fieldrow.owned-a,
  .pii-fieldrow.owned-b,
  .pii-caret,
  .pii-bars span,
  .pii-packet,
  .pii-fillbar::after,
  .pii-recvbar::after,
  .pii-plane {
    animation: none !important;
  }
  .pii-field-fill { width: 100%; }
  .pii-trow { opacity: 1; }
  .pii-bars span { height: var(--h); }
  .pii-fillbar::after,
  .pii-recvbar::after { width: 100%; }
}
</style>
